import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartDataset, ChartType, TooltipItem } from 'chart.js';

import { ReportService } from '../../../services/report/report.service';
import { LocationService } from '../../../services/location/location.service';
import { AppComponent } from '../../../app.component';
import {
  ReportChartConfig,
  ReportChartType,
  chartableYFields,
  evaluateFormula,
  formatPlotValue,
  formulaReferences,
  isCalculatedField,
  isCurrencyField,
  isDateField,
  plotValue,
} from './report-chart-config';

interface YFieldOption {
  field: any;
  on: boolean;
}

interface Accumulator {
  sum: number;
  count: number;
}

/**
 * Full-screen chart for a report: every row in the report's current date
 * range / location / filters, one X field, any number of the report's numeric
 * fields on Y. Currency fields share the left axis, counts and percentages the
 * right one, so a customer count is never flattened by dollar amounts.
 * Calculated (formula) fields are ratios and are re-derived per point from
 * the bucket's sums instead of being summed.
 *
 * Opened from the report header's chart icon; the config comes from the
 * Report template (see report-chart-config.ts). Sits below the app header,
 * which stays usable: navigation closes the chart, a location change
 * re-plots it.
 */
@Component({
  selector: 'app-report-chart',
  templateUrl: './report-chart.component.html',
  styleUrls: ['./report-chart.component.scss']
})
export class ReportChartComponent implements OnInit, AfterViewInit, OnDestroy {

  // The app header (48px + 1px rule) stays visible and usable above the chart.
  private static readonly HEADER_HEIGHT = 49;
  private static readonly BODY_CLASS = 'report-chart-open';
  // Many series (a field split by 35 locations) would bury the canvas under the
  // legend, so it is hidden past LEGEND_MAX and the tooltip carries the names.
  private static readonly LEGEND_MAX = 16;

  @ViewChild(BaseChartDirective) chartDirective: BaseChartDirective;

  chart: ReportChartConfig;
  reportName = '';
  fields: any[] = [];
  xField: any = null;
  seriesField: any = null;

  yOptions: YFieldOption[] = [];
  chartType: ReportChartType = 'line';
  bySeries = false;
  seriesAvailable = false;

  loading = true;
  rowCount = 0;
  pointCount = 0;
  rangeLabel = '';
  emptyMessage = '';

  chartData: ChartConfiguration['data'] = { labels: [], datasets: [] };
  chartOptions: ChartConfiguration['options'] = {};

  private records: any[] = [];
  private palette: string[] = [];
  private subscriptions: Subscription[] = [];

  public static showModal(chart: ReportChartConfig, reportName: string, fields: any[]): MatDialogRef<ReportChartComponent> {
    const ref = AppComponent.dialog.open(ReportChartComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: `calc(100vh - ${ReportChartComponent.HEADER_HEIGHT}px)`,
      position: { top: ReportChartComponent.HEADER_HEIGHT + 'px', left: '0' },
      hasBackdrop: false,
      panelClass: ['opaque-dialog', 'report-chart-dialog'],
      data: { chart, reportName, fields },
    });
    // Lifts the header above the overlay so its menus still open over the chart.
    document.body.classList.add(ReportChartComponent.BODY_CLASS);
    ref.afterClosed().subscribe(() => document.body.classList.remove(ReportChartComponent.BODY_CLASS));
    return ref;
  }

  constructor(
    private reportService: ReportService,
    private locationService: LocationService,
    private router: Router,
    private dialogRef: MatDialogRef<ReportChartComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.chart = data.chart;
    this.reportName = data.reportName || '';
    this.fields = data.fields || [];
  }

  ngOnInit(): void {
    const byPath: { [path: string]: any } = {};
    for (const field of this.fields) {
      if (field && field.path) {
        byPath[field.path] = field;
      }
    }
    this.xField = byPath[this.chart.xPath] || null;
    this.seriesField = this.chart.seriesPath ? byPath[this.chart.seriesPath] || null : null;
    this.chartType = this.chart.type === 'bar' ? 'bar' : 'line';

    const defaults = new Set(this.chart.yPaths || []);
    this.yOptions = chartableYFields(this.fields).map(field => ({ field, on: defaults.has(field.path) }));

    this.palette = this.resolvePalette();
    const range = this.reportService.getDateRange();
    if (range && range.startDate) {
      this.rangeLabel = this.formatDay(range.startDate) + ' – ' + this.formatDay(range.endDate);
    }
  }

  ngAfterViewInit(): void {
    // Defer past the dialog's first change detection.
    Promise.resolve().then(() => this.load());

    // The header stays live: navigating away closes the chart, a location
    // change re-plots it for the new location.
    this.subscriptions.push(
      this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe(() => this.dialogRef.close(null)),
      this.locationService.activeLocationSetObservable().subscribe(() => {
        if (!this.loading) {
          this.load();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  async load() {
    this.loading = true;
    try {
      const result = await this.reportService.loadAllRecords();
      this.records = result.records || [];
      this.rowCount = this.records.length;
      this.seriesAvailable = !!this.seriesField && this.distinctSeriesCount() > 1;
      if (!this.seriesAvailable) {
        this.bySeries = false;
      }
      this.rebuild();
    } catch (err) {
      AppComponent.showSnack(err && err.error && err.error.msg ? err.error.msg : 'Unable to load chart data.', 'OK', 5000);
      this.dialogRef.close(null);
    }
    this.loading = false;
  }

  // ---- interaction ---------------------------------------------------------

  toggleY(option: YFieldOption, on: boolean) {
    option.on = on;
    this.rebuild();
  }

  setChartType(type: ReportChartType) {
    if (this.chartType !== type) {
      this.chartType = type;
      this.rebuild();
    }
  }

  setBySeries(bySeries: boolean) {
    if (this.bySeries !== bySeries) {
      this.bySeries = bySeries;
      this.rebuild();
    }
  }

  isCurrency(field: any): boolean {
    return isCurrencyField(field);
  }

  get xIsDate(): boolean {
    return isDateField(this.xField);
  }

  get activeYFields(): any[] {
    return this.yOptions.filter(o => o.on).map(o => o.field);
  }

  close() {
    this.dialogRef.close(null);
  }

  // ---- data shaping --------------------------------------------------------

  private distinctSeriesCount(): number {
    const seen = new Set<string>();
    for (const record of this.records) {
      seen.add(String(this.valueByPath(record, this.chart.seriesPath) ?? ''));
    }
    return seen.size;
  }

  /**
   * Bucket rows by X (and series), aggregate every active Y field per bucket,
   * and emit Chart.js labels + datasets. X buckets are sorted ascending:
   * chronologically for dates, lexically otherwise (YYYY-MM-DD keys sort
   * chronologically too). Plain fields are summed (or averaged); calculated
   * fields accumulate their referenced base fields and are evaluated per bucket.
   */
  rebuild() {
    const yFields = this.activeYFields;
    const aggregate = this.chart.aggregate === 'avg' ? 'avg' : 'sum';
    const dateX = isDateField(this.xField);

    // bucketKey -> { label, sortKey, series -> accumulator key -> { sum, count } }
    // Accumulator keys: a field path, or "$" + path for a formula's base field.
    const buckets = new Map<string, { label: string; sortKey: number | string; series: Map<string, Map<string, Accumulator>> }>();

    for (const record of this.records) {
      const rawX = this.valueByPath(record, this.chart.xPath);
      if (rawX === undefined || rawX === null || rawX === '') {
        continue;
      }
      const bucketed = dateX ? this.bucketDate(rawX) : { key: String(rawX), label: String(rawX), sortKey: String(rawX) };
      if (!bucketed) {
        continue;
      }
      let bucket = buckets.get(bucketed.key);
      if (!bucket) {
        bucket = { label: bucketed.label, sortKey: bucketed.sortKey, series: new Map() };
        buckets.set(bucketed.key, bucket);
      }
      const seriesKey = this.bySeries ? String(this.valueByPath(record, this.chart.seriesPath) ?? '') : '';
      let seriesAgg = bucket.series.get(seriesKey);
      if (!seriesAgg) {
        seriesAgg = new Map();
        bucket.series.set(seriesKey, seriesAgg);
      }
      for (const field of yFields) {
        if (isCalculatedField(field)) {
          for (const ref of formulaReferences(field.formula)) {
            const raw = Number(this.valueByPath(record, ref));
            if (isNaN(raw)) {
              continue;
            }
            this.accumulate(seriesAgg, '$' + ref, raw);
          }
          continue;
        }
        const value = plotValue(field, this.valueByPath(record, field.path));
        if (value !== null) {
          this.accumulate(seriesAgg, field.path, value);
        }
      }
    }

    const ordered = Array.from(buckets.values()).sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
    const labels = ordered.map(b => b.label);

    const seriesKeys: string[] = this.bySeries
      ? Array.from(new Set(ordered.reduce((keys: string[], b) => keys.concat(Array.from(b.series.keys())), []))).sort()
      : [''];

    const datasets: ChartDataset[] = [];
    let colorIndex = 0;
    for (const field of yFields) {
      for (const seriesKey of seriesKeys) {
        const color = this.palette[colorIndex % this.palette.length];
        colorIndex++;
        const label = this.bySeries ? `${field.name} · ${seriesKey || '(blank)'}` : field.name;
        const values = ordered.map(bucket => this.bucketValue(bucket.series.get(seriesKey), field, aggregate));
        datasets.push({
          label,
          data: values,
          yAxisID: isCurrencyField(field) ? 'yCurrency' : 'yNumber',
          borderColor: color,
          backgroundColor: this.chartType === 'bar' ? this.withAlpha(color, 0.7) : this.withAlpha(color, 0.12),
          pointBackgroundColor: color,
          pointRadius: labels.length > 120 ? 0 : 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          // Monotone interpolation never overshoots between sparse points (a plain
          // bezier dips below zero between an hour with sales and an empty one).
          cubicInterpolationMode: 'monotone',
          tension: 0.3,
          fill: false,
          spanGaps: true,
          // Tooltip/tick formatting reads the field back off the dataset.
          field,
        } as any);
      }
    }

    this.pointCount = labels.length;
    this.emptyMessage = this.rowCount === 0
      ? 'No rows in the current date range.'
      : (yFields.length === 0 ? 'Turn on a field to plot.' : (labels.length === 0 ? 'No plottable values.' : ''));

    this.chartData = { labels, datasets };
    this.chartOptions = this.buildOptions(yFields, datasets.length, datasets);
    this.chartDirective?.update();
  }

  // Axis floor: zero unless the data dips below it. Chart.js's own nice-tick
  // rounding would stretch a -$0.58 refund hour down to a -$1,000 gridline.
  private axisMin(datasets: ChartDataset[], axisId: string): number {
    let min = 0;
    for (const dataset of datasets) {
      if ((dataset as any).yAxisID !== axisId) {
        continue;
      }
      for (const value of dataset.data as Array<number | null>) {
        if (typeof value === 'number' && value < min) {
          min = value;
        }
      }
    }
    return min < 0 ? min * 1.05 : 0;
  }

  private accumulate(seriesAgg: Map<string, Accumulator>, key: string, value: number) {
    const agg = seriesAgg.get(key) || { sum: 0, count: 0 };
    agg.sum += value;
    agg.count++;
    seriesAgg.set(key, agg);
  }

  /** The plotted value of one field for one bucket/series, or null for a gap. */
  private bucketValue(seriesAgg: Map<string, Accumulator> | undefined, field: any, aggregate: 'sum' | 'avg'): number | null {
    if (!seriesAgg) {
      return null;
    }
    if (isCalculatedField(field)) {
      const raw = evaluateFormula(field.formula, (path) => {
        const agg = seriesAgg.get('$' + path);
        return agg ? agg.sum : null;
      });
      return raw === null ? null : plotValue(field, raw);
    }
    const agg = seriesAgg.get(field.path);
    if (!agg || agg.count === 0) {
      return null;
    }
    return aggregate === 'avg' ? agg.sum / agg.count : agg.sum;
  }

  private buildOptions(yFields: any[], datasetCount: number, datasets: ChartDataset[]): ChartConfiguration['options'] {
    const stacked = this.bySeries && this.chartType === 'bar';
    const currencyMin = this.axisMin(datasets, 'yCurrency');
    const numberMin = this.axisMin(datasets, 'yNumber');
    const styles = getComputedStyle(document.body);
    const textColor = styles.getPropertyValue('--text-secondary').trim() || '#ccc';
    const mutedColor = styles.getPropertyValue('--text-muted').trim() || '#999';
    const gridColor = styles.getPropertyValue('--chart-grid').trim() || styles.getPropertyValue('--border-primary').trim() || 'rgba(255,255,255,0.08)';
    const tooltipBg = styles.getPropertyValue('--bg-dropdown').trim() || '#1a1a1a';

    const hasCurrency = yFields.some(isCurrencyField);
    const hasNumber = yFields.some(f => !isCurrencyField(f));
    const currencySample = yFields.find(isCurrencyField);
    const numberSample = yFields.find(f => !isCurrencyField(f));

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: datasetCount <= ReportChartComponent.LEGEND_MAX,
          position: 'top',
          labels: { color: textColor, boxWidth: 12, usePointStyle: true, pointStyle: 'circle' },
        },
        tooltip: {
          backgroundColor: tooltipBg,
          titleColor: textColor,
          bodyColor: textColor,
          borderColor: gridColor,
          borderWidth: 1,
          callbacks: {
            label: (item: TooltipItem<ChartType>) => {
              const field = (item.dataset as any).field;
              const value = item.parsed && typeof item.parsed === 'object' ? (item.parsed as any).y : item.parsed;
              return ' ' + item.dataset.label + ': ' + formatPlotValue(field, value as number);
            },
          },
        },
      },
      scales: {
        x: {
          stacked,
          ticks: { color: mutedColor, maxRotation: 45, autoSkip: true, maxTicksLimit: this.chart.xBucket === 'hour' ? 48 : 31 },
          grid: { color: gridColor },
        },
        yCurrency: {
          display: hasCurrency,
          stacked,
          position: 'left',
          min: currencyMin,
          ticks: { color: mutedColor, includeBounds: false, callback: (value) => formatPlotValue(currencySample || { type: 'currency' }, Number(value)) },
          grid: { color: gridColor },
        },
        yNumber: {
          display: hasNumber,
          stacked,
          position: hasCurrency ? 'right' : 'left',
          min: numberMin,
          ticks: { color: mutedColor, includeBounds: false, callback: (value) => formatPlotValue(numberSample || { type: 'number' }, Number(value)) },
          grid: { color: gridColor, drawOnChartArea: !hasCurrency },
        },
      },
    };
  }

  // ---- helpers -------------------------------------------------------------

  private bucketDate(raw: any): { key: string; label: string; sortKey: number } | null {
    const date = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(date.getTime())) {
      return null;
    }
    const bucket = this.chart.xBucket || 'day';
    let start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let label: string;
    if (bucket === 'hour') {
      start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours());
      label = start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) + ' '
        + start.toLocaleTimeString(undefined, { hour: 'numeric' });
    } else if (bucket === 'month') {
      start = new Date(date.getFullYear(), date.getMonth(), 1);
      label = start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    } else if (bucket === 'week') {
      start.setDate(start.getDate() - start.getDay());
      label = 'Wk of ' + start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
    } else {
      label = start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: '2-digit' });
    }
    return { key: String(start.getTime()), label, sortKey: start.getTime() };
  }

  private formatDay(value: any): string {
    const date = value && typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
  }

  private valueByPath(record: any, path: string): any {
    if (record === null || record === undefined || !path) {
      return undefined;
    }
    let current = record;
    for (const part of path.split('.')) {
      if (current === null || current === undefined || !(part in current)) {
        return undefined;
      }
      current = current[part];
    }
    return current;
  }

  /** Series colors from the theme (--chart-series-N), with a built-in fallback. */
  private resolvePalette(): string[] {
    const styles = getComputedStyle(document.body);
    const colors: string[] = [];
    for (let i = 1; i <= 12; i++) {
      const color = styles.getPropertyValue('--chart-series-' + i).trim();
      if (color) {
        colors.push(color);
      }
    }
    return colors.length > 0 ? colors : ['#d4714a', '#5b9bd5', '#8cc63f', '#f4c455', '#b06ad4', '#4fc3c3', '#e05555', '#cc8844'];
  }

  private withAlpha(color: string, alpha: number): string {
    const hex = /^#([0-9a-f]{6})$/i.exec(color);
    if (hex) {
      const n = parseInt(hex[1], 16);
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
    }
    const rgb = /^rgba?\(([^)]+)\)$/i.exec(color);
    if (rgb) {
      const parts = rgb[1].split(',').slice(0, 3).map(p => p.trim());
      return `rgba(${parts.join(', ')}, ${alpha})`;
    }
    return color;
  }
}
